import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { accessTokenAtom } from '../../atoms';
import { useAtom } from 'jotai';
import { UpdatePasswordRequest, UpdatePasswordResponse } from '../../../../functions/api/v1/users/reset-password/type';

export const RenewPasswordPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [, setAccessToken] = useAtom(accessTokenAtom);
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(true);

    useEffect(() => {
        // Get token from URL
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');
        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setIsTokenValid(false);
            setError('Invalid reset link. Please request a password reset again.');
        }
    }, [location]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Check if passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            setIsLoading(false);
            return;
        }

        try {
            const baseUrl = new URL(window.location.href).origin;
            const response = await fetch(`${baseUrl}/api/v1/users/reset-password`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    new_password: newPassword,
                } as UpdatePasswordRequest),
            });

            if (!response.ok) {
                const text = await response.text();
                try {
                    const data = JSON.parse(text);
                    setError(data.message || 'Failed to update password.');
                } catch {
                    setError(text || 'Failed to update password.');
                }
                return;
            }

            const data = await response.json() as UpdatePasswordResponse;
            setAccessToken(data.accessToken);
            navigate('/');
        } catch {
            setError('A network error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F7F8F8] flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
                <div className="flex justify-center mb-4">
                    <img src="/logo.webp" alt="Fujiyama Logo" className="h-16" />
                </div>
                <h1 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    Set New Password
                </h1>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                        {error}
                    </div>
                )}

                {!isTokenValid ? (
                    <div className="text-center">
                        <p className="mb-4">Invalid reset link.</p>
                        <button
                            onClick={() => navigate('/reset-password')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Request Password Reset Again
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                New Password
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                required
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="••••••••"
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Password must be at least 8 characters and include uppercase, lowercase, and numbers
                            </p>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-3 px-4 text-white rounded-md ${isLoading
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                } transition-colors duration-200`}
                        >
                            {isLoading ? 'Processing...' : 'Update Password'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default RenewPasswordPage;